import { handleRetrieveBlogCategories } from "../../../../../redux/public-slices/blog.slice";
import SkeletonImage from "../../../../../components/ui/SkeletonImage";
import { webinarBasicsSchema } from "../../../../../utils/velidations";
import InputFieldB from "../../../../../components/ui/InputFieldB";
import { Fragment, useEffect, useRef, useState } from "react";
import { FILE_PATH_URI } from "../../../../../utils/helper";
import { useDispatch, useSelector } from "react-redux";
import Icons from "../../../../../assets/js/Icons";

import {
  Box, Button, Checkbox, CircularProgress, FormControlLabel,
  FormGroup, Icon, InputLabel, Stack, Typography,
} from "@mui/material";

function BasicDetail({ data, setValue }) {
  const files = useRef(null);
  const dispatch = useDispatch();
  const [image, setImage] = useState(null);
  const [helperText, setHelperText] = useState({});
  const { loadings } = useSelector((state) => state.otherSlice);
  const { specialist } = useSelector((state) => state.authSlice);
  const [audiences, setAudiences] = useState(
    data?.details?.targetAudience || []
  );

  const handleImagePreview = (e) => {
    const selectedFiles = Array.from(e.target.files);
    files.current = selectedFiles;
    const previews = selectedFiles.map((f) => URL.createObjectURL(f));
    setImage(previews);
  };

  const handleretrieveAll = async () => {
    try {
      await dispatch(
        handleRetrieveBlogCategories({ limit: 100 })
      ).unwrap();
    } catch (error) {
      console.error("Error retrieving categories:", error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const form = new FormData(e.target);
    const formData = Object.fromEntries(form);
    if (files.current && files.current.length > 0) {
      formData.coverImage = files.current;
    } else if (data?.details?.coverImage) {
      formData.coverImage = data.details.coverImage;
    } else {
      formData.coverImage = null;
    }
    formData.targetAudience = Array.isArray(audiences) ? audiences : [];
    const rawSeats =
      formData?.numberOfSeats !== undefined && formData?.numberOfSeats !== ""
        ? formData.numberOfSeats
        : data?.details?.numberOfSeats;
    const numberOfSeats = parseInt(rawSeats || 0, 10);
    formData.numberOfSeats = isNaN(numberOfSeats) ? 0 : numberOfSeats;

    const validationData = { ...formData };
    if (formData.coverImage && !files.current) {
      delete validationData.coverImage;
    }

    const result = webinarBasicsSchema.safeParse(validationData);
    if (!result.success) {
      const groupedErrors = {};
      result.error.issues.forEach((issue) => {
        const key = issue.path[0];
        if (!groupedErrors[key]) {
          groupedErrors[key] = [];
        }
        groupedErrors[key].push(issue.message);
      });
      setHelperText(groupedErrors);
      return;
    }

    setHelperText({});
    data.details = formData;
    setValue("schedule");
  };

  const handleAudienceChange = (value, checked) => {
    if (checked) {
      setAudiences([...audiences, value]);
    } else {
      setAudiences(audiences.filter((a) => a !== value));
    }
  };

  const handleSelectAll = (checked) => {
    if (checked) {
      setAudiences(["individuals", "couples", "parents", "teens"]);
    } else {
      setAudiences([]);
    }
  };

  useEffect(() => {
    if (data?.details?.coverImage) {
      if (data.details.coverImage.path) {
        setImage([FILE_PATH_URI + data.details.coverImage.path]);
      } else if (Array.isArray(data.details.coverImage)) {
        const views = data.details.coverImage
          .map((img) => {
            if (img.path && typeof img.path === "string") {
              return FILE_PATH_URI + img.path;
            } else if (img instanceof File) {
              return URL.createObjectURL(img);
            }
            return null;
          })
          .filter(Boolean);
        setImage(views);
      }
    }

    handleretrieveAll();
    // eslint-disable-next-line
  }, []);

  return (
    <Fragment>
      <Typography>
        Help our community grow and heal — lead a meaningful session through
        your upcoming webinar. Share your session details and we'll take care of
        the rest!
      </Typography>

      <Typography variant="typographyA">Webinar Basics</Typography>
      <Stack spacing={2} component="form" onSubmit={handleSubmit}>
        <InputFieldB
          name="title"
          label="Title *"
          variant="outlined"
          helperText={helperText}
          placeholder="Enter Title"
          defaultValue={data?.details?.title || ""}
          inputProps={{ maxLength: 100 }}
        />

        <Box>
          <InputLabel
            sx={{
              mb: 0,
              height: 300,
              borderRadius: "8px",
              border: "1px dashed",
              placeContent: "center",
              borderColor: "primary.main",
              display: image?.length ? "block" : "grid",
            }}
          >
            {image?.length ? (
              <SkeletonImage sx={{ objectFit: "contain" }} src={image[0]} />
            ) : (
              <>
                <Icon
                  sx={{
                    fontSize: "100px",
                    color: "other.color2",
                    m: "auto",
                    svg: { display: "block" },
                  }}
                >
                  <Icons.ImageSolid />
                </Icon>
                <Typography mb={1.5} color="other.color2">
                  Upload cover image
                </Typography>
              </>
            )}
            <input
              style={{ display: "none" }}
              onChange={handleImagePreview}
              accept="image/*"
              type="file"
              name="coverImage"
            />
          </InputLabel>
          {helperText?.coverImage?.[0] && (
            <Typography mt={0.5} fontSize="14px" color="error.main">
              {helperText?.coverImage?.[0]}
            </Typography>
          )}
        </Box>

        <InputFieldB
          type="textarea"
          name="description"
          variant="outlined"
          label="Description *"
          helperText={helperText}
          placeholder="Enter description"
          defaultValue={data?.details?.description || ""}
        />

        <InputFieldB
          disabled
          variant="outlined"
          label="Specialist name *"
          placeholder="Enter specialist name"
          defaultValue={specialist?.specialist?.fullName}
        />
        <Box>
          <InputLabel labelvariant="InputFieldB">Target audience</InputLabel>
          <FormGroup row>
            <FormControlLabel
              label="Individuals"
              control={
                <Checkbox
                  checked={audiences.includes("individuals")}
                  onChange={(e) =>
                    handleAudienceChange("individuals", e.target.checked)
                  }
                />
              }
            />
            <FormControlLabel
              label="Couples"
              control={
                <Checkbox
                  checked={audiences.includes("couples")}
                  onChange={(e) =>
                    handleAudienceChange("couples", e.target.checked)
                  }
                />
              }
            />
            <FormControlLabel
              label="Parents"
              control={
                <Checkbox
                  checked={audiences.includes("parents")}
                  onChange={(e) =>
                    handleAudienceChange("parents", e.target.checked)
                  }
                />
              }
            />
            <FormControlLabel
              label="Teens"
              control={
                <Checkbox
                  checked={audiences.includes("teens")}
                  onChange={(e) =>
                    handleAudienceChange("teens", e.target.checked)
                  }
                />
              }
            />
            <FormControlLabel
              label="All"
              control={
                <Checkbox
                  checked={audiences.length === 4}
                  onChange={(e) => handleSelectAll(e.target.checked)}
                />
              }
            />
          </FormGroup>
          {helperText?.targetAudience && (
            <Typography mt={0.5} fontSize="14px" color="error.main">
              {helperText?.targetAudience}
            </Typography>
          )}
        </Box>

        <InputFieldB
          type="number"
          variant="outlined"
          name="numberOfSeats"
          helperText={helperText}
          label="Number of seats *"
          placeholder="Enter number of seats"
          defaultValue={data?.details?.numberOfSeats || ""}
        />

        <Stack direction="row" spacing={1}>
          <Button
            size="large"
            type="submit"
            variant="contained"
            sx={{ minWidth: 150 }}
            disabled={loadings?.create}
            endIcon={
              loadings?.create && (
                <CircularProgress size={20} color="text" thickness={5} />
              )
            }
          >
            Next
          </Button>
        </Stack>
      </Stack>
    </Fragment>
  );
}

export default BasicDetail;
